import { useMemo } from 'react';
import { useAuth } from './useAuth';

export interface UseRolesReturn {
  /**
   * Array of realm roles assigned to the current user
   */
  roles: string[];

  /**
   * Checks if user has a specific realm role
   * @param role - Role name to check
   * @returns True if user has the role
   */
  hasRole: (role: string) => boolean;

  /**
   * Checks if user has any of the specified roles (OR logic)
   * @param roles - Array of role names
   * @returns True if user has at least one of the roles
   */
  hasAnyRole: (roles: string[]) => boolean;

  /**
   * Checks if user has all of the specified roles (AND logic)
   * @param roles - Array of role names
   * @returns True if user has all the roles
   */
  hasAllRoles: (roles: string[]) => boolean;

  /**
   * Gets client-specific roles
   * @param clientId - Client ID to get roles for
   * @returns Array of client roles
   */
  getClientRoles: (clientId: string) => string[];
}

/**
 * Hook for working with Keycloak roles
 *
 * Provides convenient methods for checking user roles, both realm-level and client-level.
 * Must be used within a KeycloakProvider component.
 *
 * @returns Role utilities and methods
 *
 * @example Basic usage
 * ```tsx
 * import { useRoles } from 'antaeus.keycloak.react';
 *
 * function MyComponent() {
 *   const { roles, hasRole, hasAnyRole } = useRoles();
 *
 *   return (
 *     <div>
 *       <p>Your roles: {roles.join(', ')}</p>
 *       {hasRole('admin') && <AdminPanel />}
 *       {hasAnyRole(['editor', 'moderator']) && <EditButton />}
 *     </div>
 *   );
 * }
 * ```
 *
 * @example Client roles
 * ```tsx
 * function ClientRoleExample() {
 *   const { getClientRoles } = useRoles();
 *   const apiRoles = getClientRoles('my-api');
 *
 *   return <div>API roles: {apiRoles.join(', ')}</div>;
 * }
 * ```
 */
export function useRoles(): UseRolesReturn {
  const auth = useAuth();

  // Extract realm roles from user profile
  const roles = useMemo<string[]>(() => {
    if (!auth.user?.profile) {
      return [];
    }

    // Keycloak stores realm roles in realm_access.roles
    const realmAccess = auth.user.profile.realm_access as { roles?: string[] } | undefined;
    return realmAccess?.roles || [];
  }, [auth.user]);

  // Check if user has a specific role
  const hasRole = useMemo(
    () => (role: string): boolean => {
      return roles.includes(role);
    },
    [roles]
  );

  // Check if user has any of the specified roles
  const hasAnyRole = useMemo(
    () => (requiredRoles: string[]): boolean => {
      return requiredRoles.some((role) => roles.includes(role));
    },
    [roles]
  );

  // Check if user has all of the specified roles
  const hasAllRoles = useMemo(
    () => (requiredRoles: string[]): boolean => {
      return requiredRoles.every((role) => roles.includes(role));
    },
    [roles]
  );

  // Get client-specific roles
  const getClientRoles = useMemo(
    () =>
      (clientId: string): string[] => {
        if (!auth.user?.profile) {
          return [];
        }

        // Keycloak stores client roles in resource_access[clientId].roles
        const resourceAccess = auth.user.profile.resource_access as
          | Record<string, { roles?: string[] }>
          | undefined;

        return resourceAccess?.[clientId]?.roles || [];
      },
    [auth.user]
  );

  return {
    roles,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    getClientRoles,
  };
}
